import * as React from 'react'

import { useLoadCartContent } from '../hooks/useLoadCartContent'
import { useAddProductToCart } from './useAddProductToCart'
import { useRemoveCartItems } from './useRemoveCartItems'
import { useUpdateCartItems } from './useUpdateCartItems'

type TCartContent = ReturnType<typeof useLoadCartContent>['order']

type TOptions = FirstParam<typeof useLoadCartContent>

/**
 * Core logic used to manage order Cart.
 * Should be used only in the CartContentProvider (extracted here for readability).
 */
export const useCartContentState = (loadOptions: TOptions) => {
  // initial cart Q (refetch when orderId/userToken/locale changes)
  const { order, loading, refetch } = useLoadCartContent(loadOptions)

  // the most current order/cart data should always be stored in this state
  const [cartContent, setCartContent] = React.useState<TCartContent>(null)

  // update the state only if the updatedOrder contains more recent info than the one we already have
  // this could be important eg. when firing multiple Ms (editing multiple order items at once)
  // and the responses would swap somewhere on the way (network condition)
  // the older state would come back only after the more recent one & without this we would rerender based on the older state
  const updateCartContent = React.useCallback((updatedOrder: TCartContent) => {
    setCartContent((prevOrder) => getNewerData(prevOrder, updatedOrder))
  }, [])

  // * CART UPDATE CALLBACKS -----------------------------------------------------------------------
  // also react and handles its errors with the setCartError from the state above

  const { addProductToCart, addResult } = useAddProductToCart({
    orderId: loadOptions.orderId,
  })
  const { updateCartItems, updateResult } = useUpdateCartItems()
  const { removeCartItems, removeResult } = useRemoveCartItems()

  // * CART CONTENT STATE UPDATES ------------------------------------------------------------------
  // react to Q/M updates & store the result in the React state declared above

  React.useEffect(() => {
    updateCartContent(order)
  }, [order, updateCartContent])

  React.useEffect(() => {
    if (!addResult.data?.updatedOrder) {
      return
    }

    updateCartContent(addResult.data.updatedOrder)
  }, [addResult.data?.updatedOrder, updateCartContent])

  React.useEffect(() => {
    if (!updateResult.data?.updatedOrder) {
      return
    }

    updateCartContent(updateResult.data.updatedOrder)
  }, [updateCartContent, updateResult.data?.updatedOrder])

  React.useEffect(() => {
    if (!removeResult.data?.updatedOrder) {
      return
    }

    updateCartContent(removeResult.data.updatedOrder)
  }, [removeResult.data?.updatedOrder, updateCartContent])

  // * CART FETCHING STATE -------------------------------------------------------------------------
  // indicate if any of the cart operations (Q/M) is currently loading

  const isCartFetching = React.useMemo(() => {
    return loading || addResult.loading || updateResult.loading || removeResult.loading
  }, [addResult.loading, loading, removeResult.loading, updateResult.loading])

  // * RETURNED RESULTS ----------------------------------------------------------------------------

  return {
    refetch,
    cartContent,
    addProductToCart,
    updateCartItems,
    removeCartItems,
    isCartFetching,
  }
}

// returns the newer (more recent) info about the order from the two
const getNewerData = (orderA: TCartContent, orderB: TCartContent): TCartContent => {
  if (!orderA && !orderB) {
    return null
  }

  if (!orderA || !orderA.updateTimestamp) {
    return orderB
  }

  if (!orderB || !orderB.updateTimestamp) {
    return orderA
  }

  const isANewer = orderA.updateTimestamp.getTime() > orderB.updateTimestamp.getTime()

  return isANewer ? orderA : orderB
}
